import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import AuthLayout from '@/components/auth/AuthLayout';
import AuthHeader from '@/components/auth/AuthHeader';
import PasswordInput from '@/components/auth/PasswordInput';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

const ResetPassword = () => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [isValidToken, setIsValidToken] = useState(true);
    const { updatePassword, user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        // Check if user has a valid session (from reset link)
        if (user) {
            setIsValidToken(true);
        }
    }, [user]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (password !== confirmPassword) {
            toast({
                title: "Passwords don't match",
                description: "Please make sure both passwords are the same.",
                variant: "destructive",
            });
            return;
        }

        if (password.length < 6) {
            toast({
                title: "Password too short",
                description: "Password must be at least 6 characters long.",
                variant: "destructive",
            });
            return;
        }

        setLoading(true);

        try {
            const { error } = await updatePassword(password);

            if (error) {
                toast({
                    title: "Reset failed",
                    description: error.message,
                    variant: "destructive",
                });
            } else {
                toast({
                    title: "Password reset successful",
                    description: "Your password has been updated. You can now sign in with your new password.",
                });
                navigate('/auth', { replace: true });
            }
        } catch {
            toast({
                title: "An error occurred",
                description: "Please try again later.",
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    };

    if (!isValidToken) {
        return (
            <AuthLayout>
                <div className="space-y-6">
                    <AuthHeader />

                    <h2 className="text-center text-[28px] font-semibold text-black">
                        Reset Link Expired
                    </h2>

                    <p className="text-center text-gray-600">
                        The password reset link has expired. Please request a new one.
                    </p>

                    <button
                        onClick={() => navigate('/auth/forgot-password')}
                        className="w-full h-12 rounded-lg bg-[#7C3AED] text-white text-lg font-semibold hover:bg-[#6D28D9] transition-colors"
                    >
                        Request New Reset Link
                    </button>
                </div>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout>
            <div className="space-y-6">
                <AuthHeader />

                <h2 className="text-center text-[28px] font-semibold text-black">
                    Create a new password
                </h2>

                <p className="text-center text-gray-600">
                    Enter a new password for your account.
                </p>

                <form className="space-y-5" onSubmit={handleSubmit}>
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                            New Password
                        </label>
                        <PasswordInput
                            placeholder="Enter your new password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                            Confirm Password
                        </label>
                        <PasswordInput
                            placeholder="Confirm your new password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full h-12 rounded-lg bg-[#7C3AED] text-white text-lg font-semibold hover:bg-[#6D28D9] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <LoadingSpinner size="small" iconClassName="text-white" label="Resetting..." />
                        ) : (
                            'Reset Password'
                        )}
                    </button>
                </form>
            </div>
        </AuthLayout>
    );
};

export default ResetPassword;
